import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { CartItems } from "./cart/CartItems";
import { CartSummary } from "./cart/CartSummary";
import { CheckoutForm } from "./cart/CheckoutForm";
import { deliveryNoticeKey } from "./cart/DeliveryNotice";

const emptyCust = {
  name: "",
  phone: "",
  whatsapp: "",
  whatsapp_cc: "81",
  whatsapp_same: true,
  email: "",
  address: "",
  note: "",
};

export const OnlineCart = ({ channel, items, setItems, onDone }) => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [settings, setSettings] = useState(null);
  const [cust, setCust] = useState(emptyCust);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [zone, setZone] = useState("inside");
  const [loyalty, setLoyalty] = useState(null);
  const [promo, setPromo] = useState({ code: "", discount: 0 });
  const [city, setCity] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  useEffect(() => {
    if (!user) return;
    setCust((c) => ({
      ...c,
      name: c.name || user.name || "",
      phone: c.phone || user.phone || "",
      email: c.email || user.email || "",
      address: c.address || user.address || "",
    }));
  }, [user]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const cityFee = (settings?.outside_cities || []).find((c) => c.city === city)?.fee;
    const delivering = fulfillment === "delivery" && items.length > 0;
    const fee = delivering
      ? zone === "outside"
        ? (cityFee ?? settings?.outside_fee ?? 0)
        : settings?.delivery_fee || 0
      : 0;
    const minOrder = settings?.delivery_min_order || 0;
    const loyaltyPct = loyalty?.discount_pct || 0;
    return {
      subtotal,
      fee,
      minOrder,
      belowMin: delivering && subtotal < minOrder,
      loyaltyDiscount: Math.floor((subtotal * loyaltyPct) / 100),
      grandTotal: subtotal + fee,
    };
  }, [items, settings, city, zone, fulfillment, loyalty]);

  const bentoOnly = useMemo(
    () => items.some((i) => /bento|弁当/i.test(`${i.name} ${i.category || ""}`)),
    [items]
  );

  useEffect(() => {
    if (bentoOnly) setFulfillment("pickup");
  }, [bentoOnly]);

  const setQty = (id, qty) =>
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.product_id !== id)
        : prev.map((i) => (i.product_id === id ? { ...i, qty } : i))
    );

  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error(t("geo_unsupported"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCust((c) => ({ ...c, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setLocating(false);
        toast.success(t("location_shared"));
      },
      () => {
        setLocating(false);
        toast.error(t("geo_denied"));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/online-orders", {
        channel,
        fulfillment,
        zone,
        city: zone === "outside" ? city : "",
        customer: {
          name: cust.name,
          phone: cust.phone,
          whatsapp: cust.whatsapp_same ? cust.phone : cust.whatsapp,
          whatsapp_cc: cust.whatsapp_same ? "" : cust.whatsapp_cc || "81",
          email: cust.email,
          address: cust.address,
          note: cust.note,
          lat: cust.lat,
          lng: cust.lng,
        },
        items,
        lang,
        promo_code: promo.discount > 0 ? promo.code : "",
      });
      setItems([]);
      setCust(emptyCust);
      setLoyalty(null);
      setPromo({ code: "", discount: 0 });
      const notice =
        fulfillment === "delivery"
          ? (() => {
              const { key, hour } = deliveryNoticeKey(
                zone,
                settings?.delivery_cutoff_hour ?? 15,
                settings?.delivery_inside_hour ?? 21
              );
              return t(key).replace("{hour}", String(hour).padStart(2, "0"));
            })()
          : "";
      toast.success(t("order_placed"), notice ? { description: notice, duration: 9000 } : undefined);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengirim pesanan"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4"
      data-testid="online-cart"
    >
      <p className="font-display text-lg font-semibold flex items-center gap-2">
        <ShoppingBag size={18} className="text-primary" /> {t("cart")}
      </p>
      <CartItems items={items} setQty={setQty} />
      {channel === "resto" && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="cart-resto-note">
          {t("resto_note_pickup")} {t("resto_note_no_lunch_delivery")}
        </p>
      )}
      <CartSummary
        totals={totals}
        zone={zone}
        city={city}
        settings={settings}
        loyalty={loyalty}
        promo={promo}
      />
      <CheckoutForm
        cust={cust}
        setCust={setCust}
        fulfillment={fulfillment}
        setFulfillment={setFulfillment}
        zone={zone}
        setZone={setZone}
        city={city}
        setCity={setCity}
        settings={settings}
        setLoyalty={setLoyalty}
        promo={promo}
        setPromo={setPromo}
        totals={totals}
        locating={locating}
        shareLocation={shareLocation}
        busy={busy}
        submit={submit}
        itemCount={items.length}
        bentoOnly={bentoOnly}
      />
    </div>
  );
};
